import { eventTracker } from 'app/modules/Segment';
import {
  buildJusfyEventsContext,
  buildJusfyEventsProperties,
} from 'app/modules/Segment/buildJusfyEvent';
import { FeatureEnum, TContext } from 'app/modules/Segment/types';
import type { OabCaptureTriggerEvent } from 'app/modules/OabCapture/OabCaptureTriggerContext';
import { Profession } from 'app/services/oabEligibility';
import { buildApiErrorProperties } from 'app/services/tracking/apiErrorProperties';

export type OabCaptureSurface = 'dashboard_alert' | 'value_moment_modal';

export type OabCaptureDismissMethod = 'x' | 'esc';

export type OabCaptureStep = 'question' | 'oab_invite' | 'oab_form';

export type OabCaptureEntryStep = Extract<
  OabCaptureStep,
  'question' | 'oab_invite'
>;

export type OabSource = 'pending' | 'manual';

export type OabCaptureErrorAction =
  | 'profession_answer'
  | 'oab_submit'
  | 'shown_mark_persist';

export type OabCaptureTriggerSuppressedReason =
  | 'already_shown_in_session'
  | 'not_eligible'
  | 'csat_open';

const OAB_CAPTURE_EVENTS = {
  surfaceViewed: 'oab_capture_surface_viewed',
  lawyerAnswered: 'oab_capture_lawyer_answered',
  dismissed: 'oab_capture_dismissed',
  oabSubmitted: 'oab_capture_oab_submitted',
  error: 'oab_capture_error',
  triggerArmed: 'oab_capture_trigger_armed',
  triggerSuppressed: 'oab_capture_trigger_suppressed',
} as const;

const oabCaptureContext: TContext = buildJusfyEventsContext({
  component: 'oab-capture-surface',
});

export function trackOabCaptureSurfaceViewed({
  surface,
  step,
  triggerEvent = null,
  entryStep = null,
}: {
  surface: OabCaptureSurface;
  step: OabCaptureStep;
  triggerEvent?: string | null;
  entryStep?: OabCaptureEntryStep | null;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.surfaceViewed,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.surfaceViewed,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] Uma superfície de captura de OAB foi exibida ao usuário elegível. Evento passivo (exibição, não ação): surface identifica a superfície, step qual etapa foi exibida (a pergunta, o convite ou o formulário), trigger_event o momento de valor que a disparou quando houver, e entry_step por qual caminho o formulário foi aberto (a pergunta ou o convite), quando step for oab_form.',
      relevantUserActivity: false,
      extraProperties: {
        surface,
        step,
        trigger_event: triggerEvent,
        entry_step: entryStep,
      },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureLawyerAnswered({
  surface,
  profession,
}: {
  surface: OabCaptureSurface;
  profession: Profession;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.lawyerAnswered,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.lawyerAnswered,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] O usuário respondeu se é advogado. profession traz a resposta e surface a superfície em que ela foi dada; só sai depois de a profissão ser gravada com sucesso.',
      relevantUserActivity: true,
      extraProperties: { surface, profession },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureDismissed({
  surface,
  method,
  step,
}: {
  surface: OabCaptureSurface;
  method: OabCaptureDismissMethod;
  step: OabCaptureStep;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.dismissed,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.dismissed,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] O usuário adiou a captura sem responder se é advogado. step indica em que etapa da superfície ele estava e method como fechou.',
      relevantUserActivity: true,
      extraProperties: { surface, step, method },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureOabSubmitted({
  surface,
  uf,
  oabNumberFilled,
  oabSource,
}: {
  surface: OabCaptureSurface;
  uf: string;
  oabNumberFilled: boolean;
  oabSource: OabSource;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.oabSubmitted,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.oabSubmitted,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] O usuário enviou a OAB com sucesso a partir de uma superfície de captura. surface identifica a superfície, uf e oab_number_filled o que foi enviado e oab_source distingue a OAB pendente (deduzida pela Jusfy) da digitada manualmente.',
      relevantUserActivity: true,
      extraProperties: {
        surface,
        uf,
        oab_number_filled: oabNumberFilled,
        oab_source: oabSource,
      },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureError({
  surface,
  step,
  errorAction,
  error,
  profession,
}: {
  surface: OabCaptureSurface;
  step: OabCaptureStep;
  errorAction: OabCaptureErrorAction;
  error: unknown;
  profession?: Profession;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.error,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.error,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] Uma chamada de API da captura de OAB falhou. Evento passivo, não é ação do usuário: error_action identifica o ponto que falhou (profession_answer ao gravar a resposta "Você é advogado?", oab_submit ao enviar a OAB, shown_mark_persist ao gravar a marca de exibição da superfície), surface e step de onde partiu, e error_type/error_status/error_message o erro em si.',
      relevantUserActivity: false,
      extraProperties: {
        surface,
        step,
        error_action: errorAction,
        ...(profession !== undefined ? { profession } : {}),
        ...buildApiErrorProperties(error),
      },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureTriggerArmed({
  triggerEvent,
}: {
  triggerEvent: OabCaptureTriggerEvent;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.triggerArmed,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.triggerArmed,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] Um momento de valor armou o gatilho da captura de OAB. Evento passivo, é o denominador da taxa de exibição: sai uma única vez por sessão, na transição de nenhum gatilho armado para o primeiro. trigger_event identifica qual momento de valor armou (cálculo ou consulta concluída).',
      relevantUserActivity: false,
      extraProperties: { trigger_event: triggerEvent },
    }),
    context: oabCaptureContext,
  });
}

export function trackOabCaptureTriggerSuppressed({
  triggerEvent,
  reason,
}: {
  triggerEvent: OabCaptureTriggerEvent;
  reason: OabCaptureTriggerSuppressedReason;
}): void {
  eventTracker.track({
    event: OAB_CAPTURE_EVENTS.triggerSuppressed,
    properties: buildJusfyEventsProperties({
      event: OAB_CAPTURE_EVENTS.triggerSuppressed,
      feature: FeatureEnum.ONBOARDING,
      eventMeaning:
        '[Captura OAB] O gatilho armou e a superfície não pôde aparecer. Evento passivo: trigger_event é o momento de valor que armou, reason o motivo em ordem de precedência (already_shown_in_session, not_eligible, csat_open). Sai no máximo uma vez por gatilho armado.',
      relevantUserActivity: false,
      extraProperties: { trigger_event: triggerEvent, reason },
    }),
    context: oabCaptureContext,
  });
}
